import { Paragraph } from '@quickmd-llc/design-system'
import { type ReactNode } from 'react'
import { type HomeDeliveryGuideSectionId } from './homeDeliveryGuideLink'
import { Body, MedicationsLink, PriceTable, Strong } from './HomeDeliveryGuideParts'

type Section = { id: HomeDeliveryGuideSectionId; heading: string; body: ReactNode }

/**
 * QMD-4919: "How Home Delivery works", verbatim from the long guide in Figma (`52904:199763`).
 */
export const HOME_DELIVERY_GUIDE_SECTIONS: readonly Section[] = [
  {
    id: 'how-to-prescribe',
    heading: 'How to prescribe.',
    body: (
      <>
        <ol className='list-decimal pl-sp-2 text-size-2 text-text-medium'>
          <li>
            <Body>
              In ScriptSure, click <Strong>Prescribe Drug</Strong> from the Prescriptions page.
            </Body>
          </li>
          <li>
            <Body>
              Click <Strong>Order Set</Strong>. Don&apos;t use Favorite Meds or the search bar.
            </Body>
          </li>
          <li>
            <Body>Find the medication and strength, labeled by program (e.g., [MOUD], [MH]).</Body>
          </li>
          <li>
            <Body>
              Click <Strong>Prescribe</Strong>. The pharmacy is already set. Don&apos;t change it.
            </Body>
          </li>
          <li>
            <Body>
              Click <Strong>Send</Strong> to Pharmacy.
            </Body>
          </li>
        </ol>
        <Paragraph size='xs' className='text-text-light'>
          Order Sets are kept current on a per-state basis, so it shows exactly which medications and doses are
          available where you’re prescribing. If something isn’t in Order Set, it isn’t available for Home Delivery.
        </Paragraph>
      </>
    ),
  },
  {
    id: 'how-to-change',
    heading: 'How to change a prescription to Home Delivery.',
    // Figma leaves a blank line after the intro and after the list, so these three sit further apart.
    body: (
      <div className='flex flex-col gap-sp-2'>
        <Body>
          If a prescription was already sent for pickup, cancel it and then prescribe home delivery as you normally
          would.
        </Body>
        <ol className='list-decimal pl-sp-2 text-size-2 text-text-medium'>
          <li>
            <Body>
              On the Prescriptions page, click the cog (gear) icon on that prescription’s row, and then select{' '}
              <Strong>Cancel Prescription</Strong>.
            </Body>
          </li>
          <li>
            <Body>
              Prescribe home delivery from <Strong>Order Set</Strong>. Follow the steps in “How to prescribe home
              delivery” above.
            </Body>
          </li>
        </ol>
        <Body>
          If the prescription cannot be canceled in ScriptSure, reach out to the support team, who will process the
          cancellation.
        </Body>
      </div>
    ),
  },
  {
    id: 'cost',
    heading: 'What it costs.',
    body: (
      <>
        <Body>
          Cash pricing is below buprenorphine-naloxone (8-2 mg). After the visit, the patient places the order for the
          medication, and patients must pay for the medication in full. Prices for other medications are listed in the{' '}
          <MedicationsLink /> of the EMR.
        </Body>
        <PriceTable caption='Cash price of buprenorphine-naloxone (8-2 mg) by quantity' />
      </>
    ),
  },
  {
    id: 'arrival',
    heading: 'When it arrives.',
    body: (
      <Body>
        Usually 2-3 business days after the patient places the order. If the patient is concerned that they will run out
        of medication before the delivery arrives, offer a bridge script to a local pharmacy. Please check the PDMP
        first.
      </Body>
    ),
  },
  {
    id: 'sooner',
    heading: 'What if the patient needs their medication sooner?',
    body: (
      <Body>
        Send a bridge script to a local pharmacy, so the patient does not run out of medication while their shipment is
        on the way. Please check the PDMP first.
      </Body>
    ),
  },
  {
    id: 'signatures',
    heading: 'Are signatures required for delivery?',
    body: (
      <Body>
        It depends on the delivery state. If the medication ships to one of these states, an adult must be home to sign
        for it: <Strong>Arkansas, California, Georgia, Massachusetts, Mississippi, Ohio, Utah, and Virginia</Strong>.
        Everywhere else, no signature is required.
      </Body>
    ),
  },
  {
    id: 'pharmacy',
    heading: 'What pharmacy fills the medication?',
    body: (
      <Body>
        The pharmacy is set automatically in the Order Set based on the medication and state. All home delivery orders
        are being transitioned to QMD Pharmacy, but in the meantime, some prescriptions still route to Honeybee. Either
        way, please leave the pharmacy as is in the Order Set in ScriptSure.
      </Body>
    ),
  },
  {
    id: 'address',
    heading: 'Can medication be sent to a different address?',
    body: (
      <Body>
        Yes, patients can edit their delivery address when they place their order after the visit. The delivery address
        must be in the same state as their home address.
      </Body>
    ),
  },
  {
    id: 'medications',
    heading: 'What MOUD medications are available for Home Delivery?',
    body: (
      <>
        <Body>
          Buprenorphine-naloxone (Suboxone) is available in both film and tablet forms, in the quantities below. The
          Order Set in ScriptSure is kept up-to-date. Other available medications are listed in the <MedicationsLink />{' '}
          of the EMR.
        </Body>
        <PriceTable caption='Buprenorphine-naloxone (8-2 mg) quantities available for Home Delivery, with cash price' />
      </>
    ),
  },
  {
    id: 'after-the-visit',
    heading: 'What do patients do after the visit?',
    body: (
      <Body>
        After sending the prescription for home delivery (via Order Set in ScriptSure), the app will prompt the patient
        to confirm their delivery address and pay for their medication. Patients should place the order as soon as
        possible to avoid any shipping delays. After the payment, the medication will be shipped and arrive within 2-3
        business days.
      </Body>
    ),
  },
]
